import React from "react";

/**
 * CurrencyInput, paired wrapper + input where the "$" lives in its own
 * 44px zone. The dollar sign never overlaps typed text, even at the largest
 * possible value (12+ digits) on a 375px viewport.
 *
 * Usage:
 *   <CurrencyInput
 *     id="parent-agi"
 *     label="Parent adjusted gross income"
 *     value={inputs.parentAGI}
 *     onChange={(v) => set("parentAGI", v)}
 *     placeholder="0"
 *     hint="From line 11 of your 1040."
 *     testid="est-parent-agi"
 *   />
 *
 * Keeps the value as a plain digit string ("85000") in state. If you want
 * pretty formatting in the UI, set `format` to true, the visible value
 * becomes "85,000" but `onChange` still receives the digits-only string.
 */
export default function CurrencyInput({
  id,
  label,
  value,
  onChange,
  placeholder = "0",
  hint,
  testid,
  disabled = false,
  format = true,
}) {
  const digits = String(value ?? "").replace(/[^0-9]/g, "");
  const display = format && digits ? Number(digits).toLocaleString("en-US") : digits;

  const handleChange = (e) => {
    const next = e.target.value.replace(/[^0-9]/g, "");
    onChange(next);
  };

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={id} className="block text-sm text-ink-muted mb-2">
          {label}
        </label>
      )}
      <div className="cur-wrap">
        <span className="cur-prefix" aria-hidden="true">$</span>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          className="cur-input"
          value={display}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={testid}
        />
      </div>
      {hint && <p className="text-xs text-ink-dim mt-1.5">{hint}</p>}
    </div>
  );
}
